import { FC, useEffect, useState } from 'react';

import { Form, Input, Modal } from 'antd';
import TextArea from 'antd/es/input/TextArea';

import { buildMembershipMessage, REGISTRATION_FILTER_SECTIONS, REGISTRATION_SECTION_ID } from './utils';
import { IFilter } from '../../../../../app/providers/redux/slices/reports/params/filter/types';
import { getCookie } from '../../../../../helpers/utils/cookies';
import { showErrorMessage, showInfoNotification, showSuccessNotification, getApiErrorMessage } from '../../../../../helpers/utils/ui';
import { unApi } from '../../../api/endpoints/reports/unApi';
import { useActions } from '../../../hooks/useActions';
import { useTypedSelector } from '../../../hooks/useTypedSelector';
import Loading from '../cohort/components/Loading';
import Filter from '../params/filter';

type Props = {
  visible: boolean;
  onClose: () => void;
  id: number | null;
};

const getReportPathname = (): string =>
  window.location.pathname.includes('dashboard')
    ? getCookie('dashboardPathname') || window.location.pathname
    : window.location.pathname;

const parseGroupFilters = (filterJson: unknown): IFilter[] => {
  if (!filterJson) return [];
  try {
    const parsed = typeof filterJson === 'string' ? JSON.parse(filterJson) : filterJson;
    if (Array.isArray(parsed)) return parsed;
    return parsed ? [ parsed ] : [];
  } catch (err) {
    console.error('Ошибка разбора filter_json группы:', err);
    return [];
  }
};

/**
 * Модалка редактирования «Группы регистраций». Загружает данные по id, сохраняет через PUT.
 * Эталон механики — EditMetricsModal.
 */
const EditGroupModal: FC<Props> = ({ visible, onClose, id }) => {
  const [ form ] = Form.useForm();
  const { getCustomGroups } = useActions();
  const { selectedSchool } = useTypedSelector((state) => state.currentSchool);

  const [ isLoading, setIsLoading ] = useState(false);
  // Подпись индикатора: при открытии — загрузка данных, при сохранении — «Сохраняем группу…»
  const [ loadingTip, setLoadingTip ] = useState('Загрузка...');
  const [ groupFilters, setGroupFilters ] = useState<IFilter[]>([]);

  const reportPathname = getReportPathname();

  // Загрузка данных группы при открытии
  useEffect(() => {
    const loadGroup = async () => {
      if (id === null) return;
      setIsLoading(true);
      try {
        const res = await unApi.getCustomGroup(id, reportPathname);
        const data = res.data?.data ?? res.data;
        form.setFieldsValue({
          name: data?.name ?? '',
          description: data?.description ?? '',
        });
        setGroupFilters(parseGroupFilters(data?.filter_json));
      } catch (err) {
        console.error('Ошибка загрузки группы:', err);
        showErrorMessage(getApiErrorMessage(err, 'Не удалось загрузить данные группы.'));
      } finally {
        setIsLoading(false);
      }
    };
    loadGroup();
  }, [ id ]);

  const handleSubmit = async () => {
    if (!selectedSchool?.id || id === null) return;

    let values: { name: string; description?: string };
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    if (!Array.isArray(groupFilters) || groupFilters.length === 0) {
      showErrorMessage('Задайте хотя бы одно условие фильтра регистрации');
      return;
    }

    const body = {
      section_id: REGISTRATION_SECTION_ID,
      name: values.name.trim(),
      filter_json: JSON.stringify(groupFilters),
      sort: 0,
      description: values.description?.trim() || null,
    };

    try {
      // «Сохраняем…», а не «Наполняем…»: наполнение идёт на бэке только после
      // успешного сохранения. При отклонении (напр. дубль имени, 422) группа не
      // изменяется — «Наполняем группу…» вводило бы в заблуждение.
      setLoadingTip('Сохраняем группу…');
      setIsLoading(true);
      const response = await unApi.editCustomGroup(id, body, reportPathname);
      if (response.status === 200) {
        // Инфо-окно наполнения связи «группа ↔ регистрация» (кол-во + время)
        const membership = response.data?.data?.membership;
        const { kind, text } = buildMembershipMessage(membership, 'edit');
        if (kind === 'warning') showInfoNotification(text);
        else showSuccessNotification(text);
        setTimeout(
          () =>
            getCustomGroups(selectedSchool.id, reportPathname, () => {
              setIsLoading(false);
              setLoadingTip('Загрузка...');
              setTimeout(() => onClose(), 100);
            }, true),
          400
        );
      } else {
        setIsLoading(false);
        setLoadingTip('Загрузка...');
      }
    } catch (err) {
      console.error('Ошибка обновления группы:', err);
      showErrorMessage(getApiErrorMessage(err, 'Произошла ошибка во время обновления группы.'));
      setIsLoading(false);
      setLoadingTip('Загрузка...');
    }
  };

  return (
    <Modal
      style={{ top: 20 }}
      width={'55%'}
      title={<span style={{ fontWeight: 700 }}>Редактирование группы регистраций</span>}
      onCancel={onClose}
      open={visible}
      okText="Сохранить"
      cancelText="Отмена"
      onOk={handleSubmit}
    >
      <Loading loading={isLoading} tip={loadingTip}>
        <Form form={form} layout="vertical">
          {/* Название и описание в одну строку — как в окне настроек кастомных метрик */}
          <div style={{ display: 'flex', columnGap: 20 }}>
            <Form.Item
              style={{ width: '65%' }}
              label={<span style={{ fontWeight: 700 }}>Название группы</span>}
              name="name"
              rules={[ { required: true, message: 'Введите название группы' }, { max: 100, message: 'Не более 100 символов' } ]}
            >
              <Input maxLength={100} />
            </Form.Item>

            <Form.Item
              style={{ width: '35%' }}
              label={<span style={{ fontWeight: 700 }}>Описание</span>}
              name="description"
              rules={[ { max: 300, message: 'Не более 300 символов' } ]}
            >
              <TextArea maxLength={300} style={{ height: '32px', width: '100%' }} />
            </Form.Item>
          </div>

          <div style={{ fontWeight: 700, marginBottom: 8 }}>Фильтр регистрации</div>
          <div style={{ marginBottom: 16 }}>
            <Filter
              notParams={true}
              withSegment={false}
              filtersFilter={REGISTRATION_FILTER_SECTIONS}
              isCompare={false}
              withRefresh={false}
              localFilters={groupFilters}
              setLocalFilters={setGroupFilters}
            />
          </div>
        </Form>
      </Loading>
    </Modal>
  );
};

export default EditGroupModal;
